<template>
  <div class="contents">
    <GuestTopBar />

    <AppNavHeader
      variant="search"
      :reset-notifications="resetNotifications"
      @open-mobile-menu="openMobileMenu"
    >
      <template #center>
        <div
          class="bg-surface-canvas dark:bg-surface-level-800/50 dark:shadow-none inline-flex isolate rounded-lg shadow-sm"
          :aria-label="$t('commerce.searchNavigation.filtersLabel')"
        >
          <!-- Location Filter -->
          <LocationFilter
            ref="locationFilterRef"
            :current-location="currentLocation"
            :current-order-type="searchStore.orderType"
            :is-store-page="Boolean(store)"
            @update:location="handleLocationUpdate"
            @update:order-type="handleOrderTypeUpdate"
          />

          <!-- Date/Time Filter -->
          <DateTimeFilter
            ref="dateTimeFilterRef"
            :current-event-date="
              typeof searchStore.eventDateTime?.eventDate === 'string'
                ? searchStore.eventDateTime.eventDate
                : ''
            "
            :current-event-time="searchStore.eventDateTime?.eventTime ?? ''"
            :timezone="props.store?.['timezone'] ?? null"
            :order-type="searchStore.orderType"
            :store="props.store"
            :menus="props.menus"
            @update:event-date-time="handleEventDateTimeUpdate"
          />

          <!-- Event Details Filter: Group Dining is the only order type with a party size -->
          <EventDetailsFilter
            v-if="isGroupDining(searchStore.orderType)"
            ref="eventDetailsFilterRef"
            :current-guest-count="searchStore.eventDetails?.guestCount ?? ''"
            :current-event-name="searchStore.eventDetails?.eventName ?? ''"
            @update:event-details="handleEventDetailsUpdate"
          />
        </div>

        <!-- Search Bar -->
        <div class="hidden relative">
          <div
            class="bg-surface-canvas border border-border-level-300 dark:bg-surface-level-800 dark:border-border-level-600 flex items-center min-w-[300px] px-4 py-2 rounded-full"
          >
            <MagnifyingGlassIcon class="h-4 mr-3 text-content-level-400 w-4" />

            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('commerce.searchNavigation.searchPlaceholder')"
              class="bg-surface-clear dark:placeholder:text-content-level-400 dark:text-content-inverse flex-1 focus:outline-none placeholder:text-content-level-500 text-content-level-900 text-sm"
              @keyup.enter="handleSearch"
            />

            <button
              type="button"
              class="bg-primary hover:bg-primary/90 ml-2 p-1 rounded-full text-content-inverse transition-colors"
              @click="handleSearch"
            >
              <MagnifyingGlassIcon class="h-4 w-4" />
            </button>
          </div>
        </div>
      </template>

      <template #preActions>
        <NavigationPreActions
          :show-cart="showCart"
          :is-store-page="isStorePage"
          :notifications="notifications"
        />
      </template>

      <template #bottom>
        <!-- Filter Bar (only on non-search pages that need filters) -->
        <div
          v-if="showFilters && !isSearchPage"
          class="bg-surface-level-50 border-border-level-200 border-t dark:bg-surface-level-800 dark:border-border-level-700"
        >
          <div class="min-[1148px]:px-8 px-4 py-4">
            <FilterButtons :active-filters-count="activeFiltersCount" />
          </div>
        </div>

        <MobileNavDrawer
          :open="mobileMenuOpen"
          @close="closeMobileMenu"
        >
          <template #extraMobileFilters>
            <div
              v-if="showFilters && !isSearchPage"
              class="mt-6"
            >
              <button
                type="button"
                class="bg-primary flex hover:bg-primary/90 items-center justify-center p-3 rounded-lg space-x-2 text-content-inverse transition-colors w-full"
                @click="openFilterModal"
              >
                <FunnelIcon class="h-4 w-4" />

                <span>
                  {{ $t('commerce.searchNavigation.filters') }}
                </span>

                <span
                  v-if="activeFiltersCount > 0"
                  class="bg-surface-canvas font-medium px-2 py-1 rounded-full text-primary text-xs"
                  >{{ activeFiltersCount }}</span
                >
              </button>
            </div>
          </template>
        </MobileNavDrawer>

        <FilterModal
          v-if="showFilters && !isSearchPage"
          :is-open="showFilterModal"
          @close="closeFilterModal"
          @apply-filters="handleApplyFilters"
        />

        <StoreCartDrawer
          v-if="isStorePage"
          :is-open="cartStore.isStoreCartOpen"
          :store="store"
          :is-store-unavailable="props.isStoreUnavailable"
          :store-unavailable-message="props.storeUnavailableMessage"
          :check-availability="props.checkAvailability"
          @close="cartStore.closeStoreCart"
          @open-datetime-filter="openDateTimeFilter"
          @open-mobile-event-details="openMobileEventDetails"
          @open-location-filter="openLocationFilter"
          @open-event-details-filter="openEventDetailsFilter"
          @availability-failed="forwardAvailabilityFailure"
          @edit-item="forwardEditItem"
        />

        <!-- Draft Orders Drawer for search pages -->
        <DraftOrdersDrawer
          v-else
          :is-open="cartStore.isDraftOrdersOpen"
          @close="cartStore.closeDraftOrdersCart"
        />
      </template>
    </AppNavHeader>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useRoute } from '#imports'
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/vue/24/outline'

import GuestTopBar from '~/components/GuestTopBar.vue'
import AppNavHeader from '~/components/AppNavHeader.vue'
import MobileNavDrawer from '~/components/MobileNavDrawer.vue'
import DateTimeFilter from '~/components/DateTimeFilter.vue'
import DraftOrdersDrawer from '~/components/DraftOrdersDrawer.vue'
import NavigationPreActions from '~/components/NavigationPreActions.vue'
import EventDetailsFilter from '~/components/EventDetailsFilter.vue'
import LocationFilter from '~/components/LocationFilter.vue'
import StoreCartDrawer from '~/components/StoreCartDrawer.vue'
// FilterButtons and FilterModal are auto-imported or assumed to be auto-imported usually,
// but we'll import them if they are local components or assume Nuxt auto-imports them.
// Leaving out imports to let Nuxt handle auto-importing for components like `FilterButtons`.

import { useSearchStore } from '~/stores/useSearchStore'
import type { GuestCountState } from '~/utils/GuestCountState'
import { isGroupDining } from '~/utils/isGroupDining'
import { useCartStore } from '~/stores/useCartStore'
import { useShowAddToCart } from '~/composables/useShowAddToCart'
import { useNotificationsNav } from '~/composables/useNotificationsNav'
import type { CartItemView } from '~/services/api/generated'
import { Normalization } from '~/utils/normalization'

interface AvailabilityFailurePayload {
  message: () => string
  data?: unknown
}

const props = withDefaults(
  defineProps<{
    /** Controls visibility of filter controls. */
    showFilters?: boolean
    /** Number of active filters currently applied. */
    activeFiltersCount?: number
    /** Store associated with the current search. */
    store?: { timezone?: string | null } | null
    /** Indicates that the current store is unavailable. */
    isStoreUnavailable?: boolean
    /** Message explaining store unavailability. */
    storeUnavailableMessage?: string | null
    /** Menus available for the current store. */
    menus?: unknown[]
    /**
     * Runs the store page's own canonical availability check for the cart drawer's checkout,
     * instead of it duplicating the API call. Omitted on pages with no such context (e.g. search).
     */
    checkAvailability?: (() => Promise<boolean>) | null
  }>(),
  {
    activeFiltersCount: 0,
    checkAvailability: null,
    menus: () => [],
    store: null,
    storeUnavailableMessage: null
  }
)

const emit = defineEmits<{
  openMobileEventDetails: []
  availabilityFailed: [payload: AvailabilityFailurePayload]
  editItem: [item: CartItemView]
}>()

const cartStore = useCartStore()
const searchStore = useSearchStore()
const route = useRoute()

const isSearchPage = computed(() => route.path.startsWith('/search'))
const isStorePage = computed(
  () =>
    route.path.startsWith('/store/') &&
    Normalization.trimmedStringValue(route.params['slug']) !== null
)

const mobileMenuOpen = ref(false)
const showFilterModal = ref(false)
const searchQuery = ref('')
const openMobileMenu = () => {
  mobileMenuOpen.value = true
}
const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}
const openFilterModal = () => {
  showFilterModal.value = true
  mobileMenuOpen.value = false
}
const closeFilterModal = () => {
  showFilterModal.value = false
}
const forwardAvailabilityFailure = (payload: AvailabilityFailurePayload) => {
  emit('availabilityFailed', payload)
}
const forwardEditItem = (item: CartItemView) => {
  emit('editItem', item)
}
interface DateTimeFilterApi {
  openPopover?: () => void
}
interface LocationFilterApi {
  openPopover?: () => void
}
interface EventDetailsFilterApi {
  open?: () => void
}
const dateTimeFilterRef = useTemplateRef<DateTimeFilterApi>('dateTimeFilterRef')
const locationFilterRef = useTemplateRef<LocationFilterApi>('locationFilterRef')
const eventDetailsFilterRef = useTemplateRef<EventDetailsFilterApi>('eventDetailsFilterRef')

const currentLocation = computed(() => {
  const query = searchStore.searchQuery
  if (query === null) return ''
  if (typeof query === 'string') return query
  if (typeof query.q === 'string' && query.q.length > 0) return query.q
  if (typeof query.address === 'string' && query.address.length > 0) return query.address
  return ''
})

const { showAddToCart: showCart } = useShowAddToCart()

const notifications = useNotificationsNav({ enabled: showCart })
const { resetNotifications } = notifications

const handleSearch = () => {
  const trimmedQuery = searchQuery.value.trim()
  if (trimmedQuery.length > 0) {
    searchStore.searchQuery = trimmedQuery
    void navigateTo('/search')
  }
}

const handleApplyFilters = () => {
  showFilterModal.value = false
}
const handleLocationUpdate = (selectedLocation: string) => {
  searchStore.searchQuery = selectedLocation
}
const handleOrderTypeUpdate = (orderType: string) => {
  searchStore.setOrderType(orderType)
}
const handleEventDateTimeUpdate = (eventDateTime: {
  eventDate?: string | Date | null
  eventTime?: string | null
}) => {
  searchStore.eventDateTime = eventDateTime
}
const handleEventDetailsUpdate = (eventDetails: {
  guestCount: GuestCountState
  eventName: string | null
}) => {
  searchStore.setEventDetails({
    eventName: eventDetails.eventName,
    guestCount: eventDetails.guestCount.provided ? eventDetails.guestCount.value : null
  })
}

const openDateTimeFilter = () => {
  cartStore.closeStoreCart()
  setTimeout(() => {
    dateTimeFilterRef.value?.openPopover?.()
  }, 500)
}
const openMobileEventDetails = () => {
  cartStore.closeStoreCart()
  emit('openMobileEventDetails')
}
const openEventDetailsFilter = () => {
  cartStore.closeStoreCart()
  setTimeout(() => {
    if (typeof eventDetailsFilterRef.value?.open === 'function') eventDetailsFilterRef.value.open()
  }, 500)
}
// Opens the party size prompt right away; unlike the cart drawer's request, no drawer has to close first.
const openGuestCountPrompt = () => {
  eventDetailsFilterRef.value?.open?.()
}
const openLocationFilter = () => {
  cartStore.closeStoreCart()
  setTimeout(() => {
    locationFilterRef.value?.openPopover?.()
  }, 500)
}

defineExpose({ openDateTimeFilter, openGuestCountPrompt })
</script>
